import { Button } from '@shared-ui/components'
import i18next from 'i18next'
import { Component, ErrorInfo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

const renderErrorCode = (code?: string) => {
    return (
        <>
            {code ? (
                <>
                    {' '}
                    <span className="text-error-base">{code}</span>
                    <span>Error</span>{' '}
                </>
            ) : (
                <span>{i18next.t('common:errors.generic-short')}</span>
            )}
        </>
    )
}

const ErrorFallback = ({ error }: { error: Error | null }) => {
    const { t } = useTranslation(['common', 'utils'])

    return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-inactive/60">
            <div className="mx-24 flex flex-col items-center justify-center gap-3">
                <h1 className="mb-4 flex items-center gap-2 text-4xl font-bold">
                    {renderErrorCode()}
                </h1>
                <Button
                    onClick={() => window.location.reload()}
                    label={t('actions.back')}
                    variant="link"
                    color="error"
                    size="base"
                    className="mt-3 border border-background-primary bg-background-primary text-background-base hover:border-background-hover-primary hover:bg-background-hover-primary"
                />

                {process.env.NODE_ENV !== 'production' && (
                    <details className="mt-6 w-full max-w-2xl">
                        <summary className="text-error-base cursor-pointer text-sm font-medium">
                            Error details
                        </summary>
                        <pre className="bg-background-tertiary mt-2 overflow-auto rounded border border-inactive p-4 text-xs">
                            {error?.toString()}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    )
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
        }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // We can log the error to an error reporting service like crashlytics
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return <ErrorFallback error={this.state.error} />
        }

        return this.props.children
    }
}
